import { timestampFromDate } from '@bufbuild/protobuf/wkt';
import { QueryClient } from '@tanstack/react-query';
import { SystemPermission_Permission } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/user_pb';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { enqueueSnackbar } from 'notistack';
import { LoaderFunctionArgs, redirect } from 'react-router';
import { getReservoirDynamicInfoQuery } from '~/pages/ReservoirDynamicInfo/api/resevoirDynamicInfo.queries';
import { DYNAMIC_INFO_PREDICTION_TIME_PARAM } from '~/pages/ReservoirDynamicInfo/dynamic-info-page-consts';
import { ReservoirDynamicInfoListModel } from '~/pages/ReservoirDynamicInfo/models/ReservoirDynamicInfoListModel';
import { waterLevelsMetricsQuery } from '~/shared/api/metrics.queries';
import { getSystemQuery } from '~/shared/api/system.queries';
import { requireUserPermissionForSystemId } from '~/shared/auth/auth-utils';
import { createSystemReservoirRoute } from '~/shared/models/create-routes';
import { SystemModel } from '~/shared/models/system/SystemModel';
import { assertIsDefined } from '~/types/assert-type';

export type ReservoirDynamicInfoLoader = {
  reservoirDynamicInfo: ReservoirDynamicInfoListModel;
};

export function resevoirDynamicInfoLoader(queryClient: QueryClient) {
  return async ({
    request,
    params,
  }: LoaderFunctionArgs): Promise<Response | ReservoirDynamicInfoLoader> => {
    const { systemId, reservoirId } = params;
    assertIsDefined(systemId);
    assertIsDefined(reservoirId);

    await requireUserPermissionForSystemId(
      queryClient,
      request,
      SystemPermission_Permission.ADMIN,
      systemId,
    );

    const system = await queryClient.ensureQueryData(getSystemQuery(systemId));

    const systemModel = new SystemModel({ system });

    const reservoir = systemModel.reservoirById(reservoirId);

    assertIsDefined(reservoir);

    const { searchParams } = new URL(request.url);

    const time = searchParams.get(DYNAMIC_INFO_PREDICTION_TIME_PARAM);

    let timestamp;

    if (time) {
      timestamp = timestampFromDate(dayjs(time).toDate());
    }

    const waterLevels = await queryClient.ensureQueryData(
      waterLevelsMetricsQuery({
        reservoirNames: [reservoir.name],
        currentOnly: true,
      }),
    );
    if (
      waterLevels.getWaterLevelByReservoirName(reservoir.name) === undefined
    ) {
      enqueueSnackbar(t('dynamicInfoPage.error'), {
        variant: 'error',
      });
      return redirect(createSystemReservoirRoute(systemId, reservoirId));
    }

    const reservoirDynamicInfo = await queryClient.ensureQueryData(
      getReservoirDynamicInfoQuery(reservoir.name, timestamp),
    );

    return { reservoirDynamicInfo };
  };
}
